import { drop, takeWhile, uniq } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  GroupedProductionComponentInstance,
  ProductionComponentInstance,
  ScheduleLineGroup,
} from '@paragon/contract-library';
import { getRanges, notNullish } from '@paragon/utilities';

import { RootState } from '../index';
import { getActiveStation } from './getActiveStation';
import { getBundleByGuid } from './getBundleByGuid';
import { getProductionComponentInstanceByGuid } from './getProductionComponentInstanceByGuid';

export const getActiveScheduleLineGroup = createSelector(
  (state: RootState) => state.productionModels,

  (productionModels): ScheduleLineGroup | null => {
    const { activeScheduleLineGroupGuid, orderedScheduleLineGroups } = productionModels;
    if (activeScheduleLineGroupGuid === null) return null;
    return (
      orderedScheduleLineGroups.find((scheduleLineGroup) => scheduleLineGroup.guid === activeScheduleLineGroupGuid) ??
      null
    );
  },
);

export const getOrderedProductionComponentInstancesForActiveScheduleLineGroup = createSelector(
  (state: RootState) => state.productionModels,

  (productionModels): readonly ProductionComponentInstance[] => {
    const {
      activeScheduleLineGroupGuid,
      orderedBundlesByScheduleLineGroupGuid,
      orderedProductionComponentInstancesByBundleGuid,
    } = productionModels;
    if (activeScheduleLineGroupGuid === null) return [];

    const orderedBundlesForActiveScheduleGroup = orderedBundlesByScheduleLineGroupGuid[activeScheduleLineGroupGuid];
    if (!orderedBundlesForActiveScheduleGroup) return [];

    return orderedBundlesForActiveScheduleGroup.flatMap(
      (bundle) => orderedProductionComponentInstancesByBundleGuid[bundle.guid],
    );
  },
);

export const getGroupedProductionComponentInstancesForActiveScheduleLineGroup = createSelector(
  (state: RootState) => state.application.openContext,
  (state: RootState) => state.stations,
  (state: RootState) => state.productionModels,
  getBundleByGuid,
  getOrderedProductionComponentInstancesForActiveScheduleLineGroup,
  (state: RootState) => state.componentDesigns.data,

  (
    openContext,
    stations,
    productionModels,
    bundlesByGuid,
    orderedProductionComponentInstancesForActiveScheduleGroup,
    componentDesigns,
  ): GroupedProductionComponentInstance[] | null => {
    if (!openContext || openContext.type !== 'schedule-station') return null;
    const station = stations[openContext.stationGuid];
    if (!station.followsSchedule) return null;

    const { activeScheduleLineGroupGuid, analysisSetGuidToComponentDesignGuid } = productionModels;
    if (activeScheduleLineGroupGuid === null) return null;

    const groupedProductionComponentInstances: GroupedProductionComponentInstance[] = [];
    let index = 0;
    while (index < orderedProductionComponentInstancesForActiveScheduleGroup.length) {
      const remainingProductionComponentInstances = drop(
        orderedProductionComponentInstancesForActiveScheduleGroup,
        index,
      );
      const currentAnalysisSetGuid = remainingProductionComponentInstances.at(0)?.analysisSetGuid ?? null;
      if (currentAnalysisSetGuid === null) break;
      const nextRunForAnalysisSet = takeWhile(
        remainingProductionComponentInstances,
        (productionComponentInstance) => productionComponentInstance.analysisSetGuid === currentAnalysisSetGuid,
      );
      const trussName =
        componentDesigns[analysisSetGuidToComponentDesignGuid[currentAnalysisSetGuid]]?.component.name ?? '';
      const bundleGuids = uniq(
        nextRunForAnalysisSet
          .map((productionComponentInstance) => productionComponentInstance.bundleGuid)
          .filter(notNullish),
      );
      const bundleIdsArray = bundleGuids
        .map((bundleGuid) => bundlesByGuid[bundleGuid])
        .filter(notNullish)
        .map((bundle) => bundle.id);
      const bundleIds = bundleIdsArray.length === 0 ? null : getRanges(bundleIdsArray).join(', ');
      const groupedProductionComponentInstance: GroupedProductionComponentInstance = {
        guid: nextRunForAnalysisSet[0].guid,

        productionComponentInstanceGuids: nextRunForAnalysisSet.map(
          (productionComponentInstance) => productionComponentInstance.guid,
        ),
        analysisSetGuid: currentAnalysisSetGuid,
        quantity: nextRunForAnalysisSet.length,
        trussName,
        bundleIds,
      };
      groupedProductionComponentInstances.push(groupedProductionComponentInstance);
      index += nextRunForAnalysisSet.length;
    }
    return groupedProductionComponentInstances;
  },
);

export const getCurrentProductionComponentInstance = createSelector(
  getActiveStation,
  getProductionComponentInstanceByGuid,
  (activeStation, productionComponentInstanceByGuid) => {
    if (!activeStation || !activeStation.currentProductionComponentInstanceGuid) return null;
    return productionComponentInstanceByGuid[activeStation.currentProductionComponentInstanceGuid];
  },
);

export const getQuantitiesForActiveScheduleLineGroup = createSelector(
  getGroupedProductionComponentInstancesForActiveScheduleLineGroup,

  (groupedProductionComponentInstancesForActiveScheduleLineGroup): { [analysisSetGuid: string]: number } => {
    if (!groupedProductionComponentInstancesForActiveScheduleLineGroup) return {};
    return groupedProductionComponentInstancesForActiveScheduleLineGroup.reduce<{ [analysisSetGuid: string]: number }>(
      (quantitiesForActiveScheduleLineGroup, groupedProductionComponentInstances) => {
        const { analysisSetGuid, quantity } = groupedProductionComponentInstances;
        if (quantitiesForActiveScheduleLineGroup[analysisSetGuid]) {
          quantitiesForActiveScheduleLineGroup[analysisSetGuid] += quantity;
        } else {
          quantitiesForActiveScheduleLineGroup[analysisSetGuid] = quantity;
        }
        return quantitiesForActiveScheduleLineGroup;
      },
      {},
    );
  },
);
